/**
 * Shared tree navigation utilities.
 * Used by ContentPageView (auto-redirect) and useCms (section auto-nav).
 */
import type { ContentNode } from "../data/types";

/**
 * Walk the tree depth-first and return the slug path of the first node
 * that should be displayed. Stops at:
 * - api_endpoint / card nodes (always leaves)
 * - page nodes (always navigable — body fetched lazily if not in tree)
 * - category nodes that have body content (checks body, hasBody flag)
 *
 * Categories/sections without body drill into their first child.
 */
export function findFirstLeaf(nodes: ContentNode[], prefix = ""): string | null {
  for (const node of nodes) {
    const path = prefix ? `${prefix}/${node.slug}` : node.slug;
    if (node.nodeType === "api_endpoint") return path;
    if (node.nodeType === "card") return path;
    if (node.nodeType === "page") return path;
    // Category with content — stop here. Check body (when present in tree)
    // or hasBody flag (backend always includes this).
    if (node.nodeType === "category" && (node.body || node.hasBody)) return path;
    if (node.children && node.children.length > 0) {
      const child = findFirstLeaf(node.children, path);
      if (child) return child;
    }
  }
  return null;
}

/**
 * Walk the tree by slug segments to find a specific node.
 * e.g. findNodeInTree(tree, ["guides", "getting-started"])
 */
export function findNodeInTree(nodes: ContentNode[], slugSegments: string[]): ContentNode | null {
  if (slugSegments.length === 0) return null;
  const [first, ...rest] = slugSegments;
  const match = nodes.find((n) => n.slug === first);
  if (!match) return null;
  if (rest.length === 0) return match;
  return match.children ? findNodeInTree(match.children, rest) : null;
}

/**
 * A deck can sit inside any section, not only a presentations one. Tells the
 * router how to treat a path: a card, a deck (a category holding only cards),
 * or neither.
 */
export function deckRouteKind(tree: ContentNode[], path: string | undefined): "card" | "deck" | null {
  const segments = (path ?? "").replace(/\/edit$/, "").split("/").filter(Boolean);
  const node = findNodeInTree(tree, segments);
  if (!node) return null;
  if (node.nodeType === "card") return "card";
  const kids = node.children ?? [];
  if (node.nodeType === "category" && kids.length > 0 && kids.every((c) => c.nodeType === "card")) return "deck";
  return null;
}
